import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Appends collection/search pages only after a customer's explicit request.
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLAnchorElement} [viewMorePrevious]
 * @property {HTMLAnchorElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  #generation = 0;
  #loading = false;
  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard instanceof HTMLElement) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }
    this.#updateControls();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /** @param {MouseEvent} event */
  loadNext(event) {
    return this.#loadPage('next', event);
  }

  /** @param {MouseEvent} event */
  loadPrevious(event) {
    return this.#loadPage('previous', event);
  }

  /**
   * @param {'previous' | 'next'} direction
   * @param {MouseEvent} event
   */
  async #loadPage(direction, event) {
    // Modified clicks retain the anchor's normal open-in-new-tab behavior.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    if (this.#loading) return;

    const { grid } = this.refs;
    const page = this.#getPage(direction);
    if (!grid || !page) return;

    const generation = this.#generation;
    const control = direction === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    const label = control?.textContent;
    this.#loading = true;
    this.setAttribute('aria-busy', 'true');
    if (control) {
      control.setAttribute('aria-busy', 'true');
      if (control.dataset.loadingLabel) control.textContent = control.dataset.loadingLabel;
    }
    this.#updateControls();

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(page.url.toString()));
      // A filter change or disconnect must never append an obsolete response.
      if (!this.isConnected || generation !== this.#generation || this.refs.grid !== grid) return;

      const document = new DOMParser().parseFromString(html, 'text/html');
      const items = document.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
      if (!items.length) throw new Error('The requested product page contains no cards');

      const firstElement = grid.firstElementChild;
      const previousTop = firstElement ? firstElement.getBoundingClientRect().top + getScrollTop() : 0;
      if (direction === 'previous') grid.prepend(...items);
      else grid.append(...items);

      this.#aspectRatioHelper?.processNewElements();
      if (direction === 'previous' && firstElement) {
        const difference = firstElement.getBoundingClientRect().top + getScrollTop() - previousTop;
        scrollTo({ top: getScrollTop() + difference, behavior: 'instant' });
      }

      await yieldToMainThread();
      if (!this.isConnected || generation !== this.#generation) return;
      try {
        history.pushState('', '', page.url.toString());
      } catch (error) {
        // Some app hooks throw after the browser has already changed the URL.
        if (window.location.href !== page.url.toString()) throw error;
      }
    } catch (error) {
      if (this.isConnected && generation === this.#generation) {
        console.warn('Product loading failed; opening the requested page', error);
        window.location.assign(page.url.toString());
      }
    } finally {
      if (control && label != null) {
        control.textContent = label;
        control.removeAttribute('aria-busy');
      }
      if (generation === this.#generation) {
        this.#loading = false;
        this.removeAttribute('aria-busy');
        this.#updateControls();
      }
    }
  }

  /** @param {'previous' | 'next'} direction */
  #getPage(direction) {
    const { grid } = this.refs;
    if (!grid) return;
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const card = cards[direction === 'previous' ? 0 : cards.length - 1];
    if (!(card instanceof HTMLElement)) return;
    const page = Number(card.dataset.page) + (direction === 'previous' ? -1 : 1);
    if (page < 1 || page > Number(grid.dataset.lastPage)) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  #updateControls() {
    for (const direction of /** @type {const} */ (['previous', 'next'])) {
      const control = direction === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
      if (!control) continue;
      const page = this.#getPage(direction);
      const wrapper = control.closest('.product-grid__load-more');
      if (wrapper instanceof HTMLElement) wrapper.hidden = !page;
      if (page) control.href = page.url.toString();
      if (this.#loading) control.setAttribute('aria-disabled', 'true');
      else control.removeAttribute('aria-disabled');
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /** @param {Event} event */
  #handleFilterUpdate = (event) => {
    const eventSection = event.target instanceof Element ? event.target.closest('[id^="shopify-section-"]') : null;
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#generation++;
    this.#loading = false;
    this.removeAttribute('aria-busy');
  };
}
